import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import * as z from 'zod';

import type { AccountDTO, LocationDTO } from '@invenco/accounts-interface';
import { AddressType } from '@invenco/common-interface/shared';
import type { QuoteRequestDTO } from '@invenco/shipping-interface';

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardTrigger,
} from '@/components/collapsible-card';
import { Button } from '@/components/core/button';
import { CardAction, CardContent, CardHeader, CardTitle } from '@/components/core/card';
import { Form } from '@/components/core/form';
import { SelectItem } from '@/components/core/select';
import {
  AddressFormField,
  addressFormFieldDefaultValues,
  addressFormFieldSchema,
} from '@/components/forms/address-form-field';
import { CheckboxFormField } from '@/components/forms/checkbox-form-field';
import { SelectFormField } from '@/components/forms/select-form-field';
import { useGateways } from '@/gateways/GatewayProvider';
import { fetchAllPages } from '@/shared/helpers';
import { useMessages } from '@/shared/providers/MessagesProvider';

export type QuoteAddressDTO = QuoteRequestDTO['to'];

export type CreateQuoteDetails = {
  accountId: string;
  locationId: string | null;
  signatureRequired: boolean;
  to: QuoteAddressDTO;
  locations: LocationDTO[];
};

type QuoteDetailsCardProps = {
  formId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateQuote: (details: CreateQuoteDetails) => void;
};

const ALL_LOCATIONS = 'ALL';

const formSchema = z.object({
  accountId: z.string('Select an account').min(1, 'Select an account'),
  locationId: z.string(),
  signatureRequired: z.boolean(),
  address: addressFormFieldSchema,
});

type FormValues = z.infer<typeof formSchema>;

function getAccountOptions(accounts: AccountDTO[] | undefined) {
  return accounts?.filter((account) => account.id) ?? [];
}

export function QuoteDetailsCard({
  formId,
  open,
  onOpenChange,
  onCreateQuote,
}: QuoteDetailsCardProps) {
  const { accountsGateway } = useGateways();
  const messages = useMessages();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      accountId: '',
      locationId: ALL_LOCATIONS,
      signatureRequired: false,
      address: addressFormFieldDefaultValues,
    },
  });

  const { control, setValue } = form;
  const selectedAccountId = useWatch({ control, name: 'accountId' });

  const {
    data: accounts,
    isError: isAccountsError,
    isLoading: isLoadingAccounts,
    refetch: refetchAccounts,
  } = useQuery({
    queryKey: ['accounts'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getAccounts({ cursor, take }, { signal }),
      ),
  });

  const accountOptions = useMemo(() => getAccountOptions(accounts), [accounts]);

  const {
    data: allLocations,
    isError: isLocationsError,
    isLoading: isLoadingLocations,
    refetch: refetchLocations,
  } = useQuery({
    queryKey: ['quote-locations'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        accountsGateway.getLocations({ cursor, take }, { signal }),
      ),
  });

  const locations = useMemo(
    () =>
      selectedAccountId
        ? allLocations?.filter((location) => location.accountId === selectedAccountId)
        : undefined,
    [allLocations, selectedAccountId],
  );

  useEffect(() => {
    setValue('locationId', ALL_LOCATIONS);
  }, [selectedAccountId, setValue]);

  function handleSubmit(values: FormValues) {
    if (!locations?.length) {
      messages.error('No locations available for this account');
      return;
    }

    onCreateQuote({
      accountId: values.accountId,
      locationId: values.locationId === ALL_LOCATIONS ? null : values.locationId,
      signatureRequired: values.signatureRequired,
      to: { ...values.address, addressType: AddressType.RESIDENTIAL },
      locations,
    });
  }

  return (
    <CollapsibleCard aria-label="Details" open={open} onOpenChange={onOpenChange}>
      <CardHeader>
        <CardTitle>Details</CardTitle>
        <CardAction>
          <CollapsibleCardTrigger aria-label="Toggle details" />
        </CardAction>
      </CardHeader>
      <CollapsibleCardContent>
        <CardContent>
          <Form {...form}>
            <form id={formId} className="grid gap-4" onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="grid gap-4 @sm:grid-cols-2 @sm:items-start">
                <div className="grid gap-4">
                  <SelectFormField
                    control={form.control}
                    name="accountId"
                    label="Account"
                    placeholder={isLoadingAccounts ? 'Loading accounts...' : 'Select account'}
                    className="w-full"
                    disabled={isLoadingAccounts || isAccountsError || accountOptions.length === 0}
                  >
                    {accountOptions.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name}
                      </SelectItem>
                    ))}
                    {isAccountsError && (
                      <SelectItem value="accounts-error" disabled>
                        Could not load accounts
                      </SelectItem>
                    )}
                    {!isAccountsError && !isLoadingAccounts && accountOptions.length === 0 && (
                      <SelectItem value="no-accounts" disabled>
                        No accounts found
                      </SelectItem>
                    )}
                  </SelectFormField>
                  {isAccountsError && (
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => void refetchAccounts()}
                    >
                      Retry loading accounts
                    </Button>
                  )}

                  <SelectFormField
                    control={form.control}
                    name="locationId"
                    label="Location"
                    placeholder="Select location"
                    className="w-full"
                    disabled={!selectedAccountId || isLoadingLocations || isLocationsError}
                  >
                    <SelectItem value={ALL_LOCATIONS}>All</SelectItem>
                    {(locations ?? []).map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {location.name}
                      </SelectItem>
                    ))}
                    {selectedAccountId && isLocationsError && (
                      <SelectItem value="locations-error" disabled>
                        Could not load locations
                      </SelectItem>
                    )}
                    {selectedAccountId &&
                      !isLocationsError &&
                      !isLoadingLocations &&
                      !locations?.length && (
                        <SelectItem value="no-locations" disabled>
                          No locations found
                        </SelectItem>
                      )}
                  </SelectFormField>
                  {selectedAccountId && isLocationsError && (
                    <Button
                      type="button"
                      variant="outline"
                      size="xs"
                      onClick={() => void refetchLocations()}
                    >
                      Retry loading locations
                    </Button>
                  )}

                  <CheckboxFormField
                    control={form.control}
                    name="signatureRequired"
                    label="Signature required"
                  />
                </div>

                <AddressFormField
                  control={form.control}
                  name="address"
                  label="Destination address"
                />
              </div>
            </form>
          </Form>
        </CardContent>
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
}
